import { createSelector } from 'reselect';

import { operatorsSelector } from '../data/operators/selectors';
import { allParkDetailsSelector } from '../data/park/details/selectors';
import { parkResults } from '../data/park/results/selectors';
import { parkReviews } from '../data/park/reviews/selectors';
import { extraDataSelector, unitTypesSelector } from '../data/unitTypes/selectors';

import { getOperatorById } from '../data/operators/utils';
import { getParkDetailsById } from '../data/park/details/utils';
import { getParkResultsById } from '../data/park/results/utils';
import { getParkReviewsById } from '../data/park/reviews/utils';
import { getAllUnitTypeDataById } from '../data/unitTypes/utils';

import { isSameDay } from 'date-fns/isSameDay';
import { dateToDDMMYYYY, DDMMYYYYToDate } from '../../utils/date';
import { regionsSelector } from '../data/regions/selectors';
import { getRegionById } from '../data/regions/utils';
import { unavailableUnitIdsSelector } from '../search/results/selectors/selectors';
import { parkIdSelector, selectedDateSelector } from './selectors';
import { operatorIdSelector } from '../pages/browseOperatorRegions/selectors';

function getResultsData(result, date, unitTypes, extraData) {
  const nights = Object.keys(result)[0];
  const units = result[nights].map((unit) => ({
    ...unit,
    ...(getAllUnitTypeDataById(unitTypes, extraData, unit.unitType) || {}),
    arrivalDate: date,
    nights,
  }));

  return {
    date,
    nights,
    lowestPrice: units.map((unit) => +unit.unitPrice).sort((a, b) => a - b)[0],
    units,
  };
}

export const parkDetailsSelector = createSelector([allParkDetailsSelector, parkIdSelector], (allParkDetails, parkId) =>
  getParkDetailsById(allParkDetails, parkId),
);

export const parkOperatorSelector = createSelector([parkDetailsSelector, operatorsSelector], (parkDetails, operators) =>
  getOperatorById(operators, parkDetails?.opId),
);

export const parksRegionNameSelector = createSelector(
  [parkDetailsSelector, regionsSelector],
  (parkDetails, regions) => {
    const regionId = parkDetails?.regn;
    const region = getRegionById(regions, regionId);
    return region?.name || null;
  },
);

export const resultsSelector = (state) => getParkResultsById(parkResults(state), parkIdSelector(state));

export const hasResultsSelector = (state) => {
  const results = resultsSelector(state)?.results;
  return results && Object.keys(results).length > 0;
};

export const reviewsSelector = (state) => getParkReviewsById(parkReviews(state), parkIdSelector(state));

// For use on Operator Regions Page
export const operatorNameSelector = createSelector(operatorsSelector, operatorIdSelector, (operators, operatorId) => {
  return getOperatorById(operators, operatorId)?.name;
});

const sortByDate = (arr) => {
  return arr.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
};

const markCheapestDates = (dates) => {
  if (!dates.length) return dates;
  const cheapestPrice = Math.min(...dates.map((d) => d.lowestPrice));
  return dates.map((d) => ({ ...d, isCheapest: d.lowestPrice === cheapestPrice }));
};

export const processedResultsSelector = createSelector(
  resultsSelector,
  selectedDateSelector,
  unitTypesSelector,
  extraDataSelector,
  unavailableUnitIdsSelector,
  (parkResults, selectedDateStr, unitTypes, extraData, unavailableUnitIds) => {
    const selectedDateDate = new Date(selectedDateStr);
    const results = parkResults?.results;

    if (!results) return undefined;

    const all = {};

    Object.keys(results).forEach((dateKey) => {
      const baseData = getResultsData(results[dateKey], DDMMYYYYToDate(dateKey), unitTypes, extraData);

      all[dateKey] = {
        ...baseData,
        units: baseData.units.filter((unit) => !unavailableUnitIds.includes(unit.unitId)),
      };
    });

    const selectedDate = !isNaN(selectedDateDate.getTime()) ? all[dateToDDMMYYYY(selectedDateDate)] : undefined;
    const allDates = Object.keys(all)?.length ? markCheapestDates(sortByDate(Object.values(all))) : [];

    const isAnAlternativeDate = !isSameDay(allDates[0]?.date, selectedDateDate);
    const isMultipleDates = allDates.length > 1;
    const singleDateIsNotSelected = allDates.length === 1 && isAnAlternativeDate;
    const showAlternativeDates = isMultipleDates || singleDateIsNotSelected;

    return {
      allDates,
      selectedDate,
      showAlternativeDates,
    };
  },
);
